/**
 * Notification Detail Drawer
 * 
 * Shows detailed information about a notification:
 * - Channel-specific content (EMAIL: HTML preview, SMS: message, etc.)
 * - Event timeline (lifecycle tracking)
 * - Attachments and download tokens
 * - Retry action for DLQ notifications
 */

import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Button,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  toast
} from '@etherisc/ui-kit';
import { 
  XIcon, 
  RefreshCwIcon, 
  MailIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon
} from 'lucide-react';

interface NotificationEvent {
  id: string;
  type: string;
  occurredAt: string;
  verified: boolean;
  payload?: Record<string, unknown>;
}

interface NotificationDetails {
  id: string;
  channel: 'EMAIL' | 'SMS' | 'SLACK';
  to: Array<{email?: string; phoneNumber?: string; name?: string}>;
  templateName: string;
  templateVersion: string;
  channelMetadata: Record<string, unknown>;
  status: string;
  sentAt?: string;
  provider?: string;
  providerMessageId?: string;
  attempts: number;
  events: NotificationEvent[];
  createdAt?: string;
}

interface NotificationDetailDrawerProps {
  notificationId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onRetry?: (id: string) => void;
}

export function NotificationDetailDrawer({
  notificationId,
  isOpen,
  onClose,
  onRetry
}: NotificationDetailDrawerProps) {
  const [notification, setNotification] = useState<NotificationDetails | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (notificationId && isOpen) {
      loadNotificationDetails();
    }
  }, [notificationId, isOpen]);

  const loadNotificationDetails = async () => {
    if (!notificationId) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/notifications/${notificationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load notification details');
      }

      const data = await response.json();
      setNotification(data.data);
    } catch (error) {
      console.error('Error loading notification:', error);
      toast.error('Failed to load notification details');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    if (!notification) return;

    try {
      const response = await fetch(`/api/notifications/retry/${notification.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to retry notification');
      }

      toast.success('Notification requeued for retry');
      onRetry?.(notification.id);
      onClose();
    } catch (error) {
      console.error('Error retrying notification:', error);
      toast.error('Failed to retry notification');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'DELIVERED': return <CheckCircleIcon size={20} className="text-green-600" />;
      case 'SENT': return <MailIcon size={20} className="text-blue-600" />;
      case 'BOUNCED':
      case 'FAILED':
      case 'DLQ': return <XCircleIcon size={20} className="text-red-600" />;
      default: return <ClockIcon size={20} className="text-gray-600" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle>Notification Details</SheetTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
            >
              <XIcon size={16} />
            </Button>
          </div>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : notification ? (
          <div className="space-y-6 mt-6">
            {/* Status Header */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(notification.status)}
                    <div>
                      <Badge variant={
                        notification.status === 'DELIVERED' ? 'success' :
                        notification.status === 'SENT' ? 'info' :
                        ['BOUNCED', 'FAILED', 'DLQ'].includes(notification.status) ? 'error' :
                        'neutral'
                      }>
                        {notification.status}
                      </Badge>
                      <p className="text-sm text-gray-600 mt-1">
                        {notification.attempts} attempt(s)
                      </p>
                    </div>
                  </div>
                  {notification.status === 'DLQ' && (
                    <Button
                      size="sm"
                      onClick={handleRetry}
                    >
                      <RefreshCwIcon size={14} className="mr-2" />
                      Retry
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* EMAIL Content */}
            {notification.channel === 'EMAIL' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Email Content</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Subject</label>
                    <p className="text-gray-900">
                      {(notification.channelMetadata as {subject?: string}).subject || 'No subject'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Recipients</label>
                    <div className="mt-1 space-y-1">
                      {notification.to.map((recipient, i) => (
                        <p key={i} className="text-sm text-gray-900">
                          {recipient.email} {recipient.name && `(${recipient.name})`}
                        </p>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Template</label>
                    <p className="text-sm text-gray-900">
                      {notification.templateName} v{notification.templateVersion}
                    </p>
                  </div>
                  {notification.provider && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Provider</label>
                      <p className="text-sm text-gray-900">
                        {notification.provider} {notification.providerMessageId && `(${notification.providerMessageId})`}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* SMS Content */}
            {notification.channel === 'SMS' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">SMS Content</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Recipients</label>
                    <div className="mt-1 space-y-1">
                      {notification.to.map((recipient, i) => (
                        <p key={i} className="text-sm text-gray-900">
                          {recipient.phoneNumber} {recipient.name && `(${recipient.name})`}
                        </p>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Template</label>
                    <p className="text-sm text-gray-900">
                      {notification.templateName} v{notification.templateVersion}
                    </p>
                  </div>
                  {notification.provider && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Provider</label>
                      <p className="text-sm text-gray-900">
                        {notification.provider} {notification.providerMessageId && `(${notification.providerMessageId})`}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Event Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Event Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {notification.events.map((event, index) => (
                    <div key={event.id} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-3 h-3 rounded-full ${
                          event.type === 'DELIVERED' ? 'bg-green-600' :
                          event.type === 'SENT' ? 'bg-blue-600' :
                          event.type === 'FAILED' ? 'bg-red-600' :
                          'bg-gray-400'
                        }`} />
                        {index < notification.events.length - 1 && (
                          <div className="w-0.5 h-full bg-gray-300 mt-1 flex-1" />
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{event.type}</span>
                          {event.verified && (
                            <CheckCircleIcon size={14} className="text-green-600" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {formatTimestamp(event.occurredAt)}
                        </p>
                        {event.payload && Object.keys(event.payload).length > 0 && (
                          <details className="mt-2">
                            <summary className="text-sm text-gray-500 cursor-pointer">
                              Event details
                            </summary>
                            <pre className="text-xs bg-gray-50 p-2 rounded mt-1 overflow-auto">
                              {JSON.stringify(event.payload, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Timestamps */}
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-gray-600">Created</label>
                    <p className="font-medium">
                      {notification.createdAt ? formatTimestamp(notification.createdAt) : 'Unknown'}
                    </p>
                  </div>
                  {notification.sentAt && (
                    <div>
                      <label className="text-gray-600">Sent</label>
                      <p className="font-medium">{formatTimestamp(notification.sentAt)}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No notification selected
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

